"use client";

import { CalendarToday } from "@mui/icons-material";
import { add, format } from "date-fns";
import * as React from "react";
import { useCallback } from "react";

import { RenderIf } from "../../common/RenderIf";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { Calendar, CalendarProps } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { TimePicker } from "./timepicker";

interface DateTimePickerProps {
	date?: Date;
	placeholder?: string;
	setDate: (date?: Date) => void;
	withTime?: boolean;
	triggerClassName?: string;
	calendarProps?: Omit<CalendarProps, "mode"> & { mode?: "single" | "range" | "multiple" };
	dateLabelFormat?: string;
}

function DatePicker({
	date,
	setDate,
	dateLabelFormat = "MM/dd/yyyy HH:mm",
	withTime,
	placeholder = "Pick a date",
	triggerClassName,
	calendarProps,
}: DateTimePickerProps) {
	/**
	 * carry over the current time when a user clicks a new day
	 * instead of resetting to 00:00
	 */
	const handleSelect = useCallback(
		(newDay: Date | undefined) => {
			if (!newDay) return;

			if (!date) {
				setDate(newDay);
				return;
			}
			const diff = newDay.getTime() - date.getTime();
			const diffInDays = diff / (1000 * 60 * 60 * 24);
			const newDateFull = add(date, { days: Math.ceil(diffInDays) });
			setDate(newDateFull);
		},
		[date, setDate],
	);

	return (
		<Popover>
			<PopoverTrigger asChild>
				<Button
					variant={"outline"}
					className={cn(
						"w-[280px] justify-start text-left font-normal",
						{
							"text-muted-foreground": !date,
						},
						triggerClassName,
					)}
				>
					{date ? format(date, dateLabelFormat) : <span>{placeholder}</span>}
					<CalendarToday className="ml-2 !size-4" />
				</Button>
			</PopoverTrigger>

			<PopoverContent className="w-auto p-0">
				<Calendar
					selected={date}
					onSelect={(d?: Date) => handleSelect(d)}
					autoFocus
					{...calendarProps}
				/>
				<RenderIf condition={Boolean(withTime)}>
					<div className="p-3 border-t border-border">
						<TimePicker setDate={setDate} date={date} />
					</div>
				</RenderIf>
			</PopoverContent>
		</Popover>
	);
}

DatePicker.displayName = "DatePicker";

export { DatePicker };
